import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calculator, FileText, Scale } from "lucide-react";
import Seo from "@/components/Seo";
import { CALCULATORS, CHECKED_ON, CONTRACT_STUDIO_URL } from "@/data/calculators";
import { lowestForService, formatPrice } from "@/data/packages";

// Shared frame for every calculator page: hero, the calculator itself,
// the working, sources, FAQs, and the two routes onward (Electra for help,
// Contract Studio for the matching document). FAQ answers use <details> so
// the text is in the prerendered HTML for crawlers, not mounted on click.
export default function CalculatorShell({ calc, children, howItWorks, example }) {
  const origin = window.location.origin;
  const helpService = (calc.helpLink || "").startsWith("/services/") ? calc.helpLink.split("/")[2] : "";
  const helpPackage = helpService ? lowestForService(helpService) : null;
  const pageUrl = `${origin}/calculators/${calc.slug}`;
  const others = CALCULATORS.filter((c) => c.slug !== calc.slug);
  const [jurisdiction, setJurisdiction] = useState("gb");

  return (
    <main data-testid={`calculator-page-${calc.slug}`}>
      <Seo
        title={calc.metaTitle}
        siteName="Electra HR"
        description={calc.metaDescription}
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              "@id": `${pageUrl}#app`,
              name: calc.name,
              url: pageUrl,
              description: calc.metaDescription,
              applicationCategory: "BusinessApplication",
              operatingSystem: "Any",
              isAccessibleForFree: true,
              offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
              publisher: { "@id": `${origin}/#organization` },
              inLanguage: "en-GB",
            },
            {
              "@type": "WebPage",
              "@id": pageUrl,
              url: pageUrl,
              name: calc.name,
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              dateModified: CHECKED_ON.iso,
              citation: calc.sources.map((s) => s.url),
            },
            {
              "@type": "FAQPage",
              mainEntity: calc.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "Calculators", item: `${origin}/calculators` },
                { "@type": "ListItem", position: 3, name: calc.name, item: pageUrl },
              ],
            },
          ],
        }}
      />

      <section className="grain relative overflow-hidden bg-ink pb-14 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <nav aria-label="Breadcrumb" className="font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
            <Link to="/" className="hover:text-pink">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/calculators" className="hover:text-pink">Calculators</Link>
          </nav>
          <p className="eyebrow mt-6 text-pink">Free UK employment calculator</p>
          <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight">
            {calc.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/70">{calc.intro}</p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
            Figures checked {CHECKED_ON.label} · England, Wales and Scotland
          </p>
        </div>
      </section>

      <section className="bg-paper py-12 sm:py-16">
        <div className="container-x grid gap-10 lg:grid-cols-[1fr_340px]">
          <div>
            <div className="border-2 border-ink bg-white p-6 sm:p-8 shadow-[6px_6px_0_0_#0F0F1A]">
              <p className="flex items-center gap-2 eyebrow text-ink/50">
                <Calculator size={16} className="text-pink" /> Calculator
              </p>
              <div className="mt-6">
                <Field label="Where does the employee work?" htmlFor="jurisdiction">
                  <select id="jurisdiction" className={inputClass} value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} data-testid="calc-jurisdiction">
                    <option value="gb">England, Wales or Scotland</option>
                    <option value="ni">Northern Ireland</option>
                  </select>
                </Field>
              </div>
              <div className="mt-6">
                {jurisdiction === "gb" ? children : (
                  <div className="border-l-4 border-pink bg-pink/10 p-5 text-sm leading-relaxed text-ink/80" data-testid="calc-ni-notice">
                    Northern Ireland has its own employment legislation, limits and holiday rules, and this calculator does not
                    cover them yet. <Link to="/contact" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">Ask us</Link> and
                    we will work it out for you.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-14 space-y-12">
              <section>
                <h2 className="flex items-center gap-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                  <Scale size={24} className="text-pink" /> How it is worked out
                </h2>
                <div className="prose-calc mt-5 space-y-4 text-base leading-relaxed text-ink/75">{howItWorks}</div>
              </section>

              {example && (
                <section>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Worked example</h2>
                  <div className="mt-5 border-l-4 border-pink bg-pink/10 p-6 text-base leading-relaxed text-ink/80">{example}</div>
                </section>
              )}

              <section>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Questions employers ask</h2>
                <div className="mt-5 divide-y divide-ink/15 border-y border-ink/15">
                  {calc.faqs.map((f, i) => (
                    <details key={f.q} className="group py-5" data-testid={`calc-faq-${i}`}>
                      <summary className="cursor-pointer list-none font-display text-base sm:text-lg font-semibold">
                        <span className="mr-2 text-pink group-open:hidden">+</span>
                        <span className="mr-2 hidden text-pink group-open:inline">−</span>
                        {f.q}
                      </summary>
                      <p className="mt-3 text-sm sm:text-base leading-relaxed text-ink/70">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight">
                  <BookOpen size={22} className="text-pink" /> Sources
                </h2>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-ink/70">
                  {calc.sources.map((s) => (
                    <li key={s.url}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline decoration-pink decoration-2 underline-offset-4 hover:text-ink">
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>

              <p className="border-t border-ink/15 pt-6 text-xs leading-relaxed text-ink/55">
                This calculator gives a general estimate based on the law and statutory figures in force on {CHECKED_ON.label} for
                England, Wales and Scotland. It is not legal advice and does not take account of your contract, your policies or
                any special circumstances. Contractual terms can give more than the statutory minimum, never less. Northern Ireland
                has its own rules and limits and is not covered. Nothing you enter is stored or sent anywhere: the sums are done in your browser.
              </p>
            </div>
          </div>

          <aside className="h-fit space-y-6 lg:sticky lg:top-28">
            <div className="border-2 border-ink bg-ink p-7 text-paper">
              <p className="eyebrow text-pink">Not a simple case?</p>
              <p className="mt-4 font-display text-xl font-semibold">{calc.helpTitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-paper/70">{calc.helpText}</p>
              {helpPackage ? (
                <p data-testid="calc-help-price" className="mt-4 text-sm text-paper/80">
                  <span className="font-semibold text-pink">{formatPrice(helpPackage)}</span>, fixed before any work starts.
                </p>
              ) : null}
              <Link
                to={calc.helpLink}
                data-testid="calc-help-cta"
                className="group mt-6 inline-flex items-center gap-2 bg-pink px-5 py-3 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5"
              >
                Get a fixed quote <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="border-2 border-ink bg-pink/10 p-7">
              <p className="flex items-center gap-2 eyebrow text-ink/50">
                <FileText size={15} className="text-pink" /> Get it in writing
              </p>
              <p className="mt-4 font-display text-lg font-semibold">{calc.docTitle}</p>
              <p className="mt-2 text-sm leading-relaxed text-ink/70">{calc.docText}</p>
              <a
                href={`${CONTRACT_STUDIO_URL}${calc.docPath}`}
                target="_blank"
                rel="noopener"
                data-testid="calc-contract-studio-cta"
                className="group mt-5 inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-bold text-ink transition-colors hover:bg-ink hover:text-paper"
              >
                See the documents <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="mt-3 text-xs text-ink/50">Lawyer-drafted templates from The Contract Studio.</p>
            </div>

            <div className="border border-ink/15 p-6">
              <p className="eyebrow text-ink/50">More calculators</p>
              <ul className="mt-4 space-y-3 text-sm">
                {others.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/calculators/${c.slug}`} className="group inline-flex items-center gap-2 font-semibold text-ink hover:text-bolt">
                      {c.name} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

// Small form primitives shared by the calculator pages.
export const Field = ({ label, hint, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-ink">{label}</label>
    {hint && <p className="mt-1 text-xs leading-relaxed text-ink/55">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

export const inputClass =
  "w-full border-2 border-ink/20 bg-paper px-4 py-3 text-base text-ink outline-none transition-colors focus:border-pink";

export const Result = ({ items, notes = [], testId = "calc-result" }) => (
  <div className="mt-8 border-t-2 border-ink pt-6" data-testid={testId} aria-live="polite">
    <dl className="grid gap-4 sm:grid-cols-2">
      {items.map((it) => (
        <div key={it.label} className={it.big ? "sm:col-span-2 bg-ink p-5 text-paper" : "bg-paper p-5"}>
          <dt className={`eyebrow ${it.big ? "text-pink" : "text-ink/50"}`}>{it.label}</dt>
          <dd className={`mt-2 font-display font-semibold ${it.big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{it.value}</dd>
          {it.sub && <p className={`mt-1 text-xs ${it.big ? "text-paper/60" : "text-ink/55"}`}>{it.sub}</p>}
        </div>
      ))}
    </dl>
    {notes.length > 0 && (
      <ul className="mt-5 space-y-2 text-sm leading-relaxed text-ink/70">
        {notes.map((n) => (
          <li key={n} className="flex gap-2"><span className="text-pink">•</span><span>{n}</span></li>
        ))}
      </ul>
    )}
  </div>
);
